import {
  type ClientResponse,
  type Conversation,
  type ConversationContext,
  type ConversationParticipant,
  type StartConversationResponse,
  type TriggerParams,
} from '@stripe-internal/conversations-client';

import type {Api} from '../../core/lib/api/api';
import type {AppConfig, AppProfile} from '../../core/types';
import type {
  ControllerError,
  ControllerResult,
  ConversationOverview,
  CreateConversationParams,
  ListConversationFilters,
} from './types';
import type {ConversationsBackend} from '../app/types';

type ErrorInput = Omit<ControllerError, 'type'>;

const success = <T>(data: T) => ({type: 'success' as const, data});

const error = ({
  code,
  error: errorMessage,
  errorId,
}: ErrorInput): ControllerError => ({
  type: 'error',
  error: errorMessage,
  ...(code === undefined ? {} : {code}),
  ...(errorId === undefined ? {} : {errorId}),
});

const fromClientError = (
  response: Pick<ClientResponse, 'code' | 'error'> & {
    sentryEventId?: string;
  },
): ControllerError =>
  error({
    code: response.code,
    error: response.error ?? 'Unknown conversation controller error',
    errorId: response.sentryEventId,
  });

export function buildConversationsBackendApi({
  getConfig,
  getProfile,
  getApi,
  createConversation,
  loadConversation,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  createConversation: (params: {
    participant: ConversationParticipant;
    context: ConversationContext;
    data?: Record<string, unknown>;
  }) => Promise<StartConversationResponse>;
  loadConversation: (params: {
    conversationId: string;
    participant: ConversationParticipant;
    context: ConversationContext;
  }) => Promise<StartConversationResponse>;
}): Pick<ConversationsBackend, 'create' | 'load' | 'list' | 'end'> {
  function getTriggerParams(params: CreateConversationParams): TriggerParams {
    const config = getConfig();
    const initialMessage = params.initialMessage?.trim();
    return {
      triggerPoint:
        params.context?.triggerParams?.triggerPoint ??
        config.triggerPoint ??
        'unspecified',
      ...params.context?.triggerParams,
      ...(initialMessage ? {initialQuery: initialMessage} : {}),
    };
  }

  function getContext(
    params: CreateConversationParams = {},
  ): ConversationContext {
    const config = getConfig();
    const triggerParams = getTriggerParams(params);
    const participant = getParticipant();

    return {
      userId: participant.id,
      audience: config.audience,
      entryPoint: config.entryPoint,
      locale: config.locale,
      ...params.context,
      triggerParams,
    };
  }

  function getParticipant(): ConversationParticipant {
    const {visitor} = getProfile();

    const participant: ConversationParticipant = {
      id: visitor.id,
      role: 'user',
      displayName: visitor.displayName || 'User',
    };

    return participant;
  }

  return {
    async create(
      params: CreateConversationParams = {},
    ): ControllerResult<Conversation> {
      const response = await createConversation({
        participant: getParticipant(),
        context: getContext(params),
        data: params.data,
      });
      if ('error' in response) {
        return fromClientError(response);
      }
      return success(response.data.conversation);
    },
    async end(id: string): ControllerResult<void> {
      const response = await getApi().endConversation(id);
      if (response.type === 'error') {
        return error(response);
      }
      return success(undefined);
    },
    async load(id: string): ControllerResult<Conversation> {
      const response = await loadConversation({
        conversationId: id,
        participant: getParticipant(),
        context: getContext(),
      });
      if ('error' in response) {
        return fromClientError(response);
      }
      return success(response.data.conversation);
    },
    async list(
      filters: ListConversationFilters = {},
    ): ControllerResult<ConversationOverview[]> {
      const config = getConfig();
      const api = getApi();

      const response = await api.listConversations({
        audience: config.audience,
        entryPoint: config.entryPoint,
        status: filters.status,
        limit: filters.limit,
        triggerPoint: filters.triggerPoint,
        conversationIds: filters.conversationIds,
      });

      if (response.type === 'error') {
        return error(response);
      }

      return success(response.conversations);
    },
  };
}
